import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { AlertTriangle, Check, Clock3, Eye, FileVideo2, Inbox, RotateCcw, Save, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  adminApi,
  type AdminApplication,
  type ApplicationCapacity,
  type ApplicationStatus,
} from '@/api';
import {
  createPassExpiryDraft,
  isPassExpiryValid,
  PassExpiryPicker,
  toPassExpiryPayload,
  type PassExpiryDraft,
} from '@/components/admin/PassExpiryPicker';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { NumberInput } from '@/components/ui/number-input';
import { Select } from '@/components/ui/select';
import { formatDateTime } from '@/lib/utils';

const PER_PAGE = 20;

type SettingsDraft = Pick<ApplicationCapacity, 'isEnabled' | 'totalSlots' | 'maxVideoSizeMb'>;

const statusLabel: Record<ApplicationStatus, string> = {
  pending: 'На рассмотрении',
  accepted: 'Принята',
  rejected: 'Отклонена',
};

const statusVariant: Record<ApplicationStatus, 'secondary' | 'success' | 'danger'> = {
  pending: 'secondary',
  accepted: 'success',
  rejected: 'danger',
};

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}

function ApplicationVideo({ application }: { application: AdminApplication }) {
  const [error, setError] = useState(false);

  useEffect(() => {
    setError(false);
  }, [application.id]);

  if (error) {
    return <p className="rounded-lg border border-eol-red/20 bg-eol-red/5 p-4 text-sm text-eol-red">Не удалось загрузить видео.</p>;
  }
  return (
    <video
      controls
      preload="metadata"
      crossOrigin="use-credentials"
      src={adminApi.applications.videoUrl(application.id)}
      onError={() => setError(true)}
      className="aspect-video max-h-[58vh] w-full rounded-lg bg-black"
    >
      Ваш браузер не поддерживает просмотр видео.
    </video>
  );
}

export function AdminApplications() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<'all' | ApplicationStatus>('all');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<AdminApplication | null>(null);
  const [accepting, setAccepting] = useState<AdminApplication | null>(null);
  const [rejecting, setRejecting] = useState<AdminApplication | null>(null);
  const [deleting, setDeleting] = useState<AdminApplication | null>(null);
  const [resetSlotsOpen, setResetSlotsOpen] = useState(false);
  const [acceptServerId, setAcceptServerId] = useState('');
  const [acceptExpiry, setAcceptExpiry] = useState<PassExpiryDraft>(() => createPassExpiryDraft());
  const [settingsDraft, setSettingsDraft] = useState<SettingsDraft>({
    isEnabled: false,
    totalSlots: 0,
    maxVideoSizeMb: 50,
  });
  const [settingsDirty, setSettingsDirty] = useState(false);

  const settingsQuery = useQuery({
    queryKey: ['admin', 'applications', 'settings'],
    queryFn: adminApi.applications.settings,
    refetchInterval: 15_000,
  });
  const listQuery = useQuery({
    queryKey: ['admin', 'applications', 'list', status, page],
    queryFn: () =>
      adminApi.applications.list({
        status: status === 'all' ? undefined : status,
        page,
        perPage: PER_PAGE,
      }),
    refetchInterval: 15_000,
  });
  const serversQuery = useQuery({
    queryKey: ['admin', 'servers'],
    queryFn: adminApi.servers.list,
    enabled: Boolean(accepting),
  });

  useEffect(() => {
    if (!settingsQuery.data || settingsDirty) return;
    setSettingsDraft({
      isEnabled: settingsQuery.data.isEnabled,
      totalSlots: settingsQuery.data.totalSlots,
      maxVideoSizeMb: settingsQuery.data.maxVideoSizeMb,
    });
  }, [settingsDirty, settingsQuery.data]);

  const updateSettingsDraft = (patch: Partial<SettingsDraft>) => {
    setSettingsDirty(true);
    setSettingsDraft((current) => ({ ...current, ...patch }));
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'applications'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'passes'] });
    queryClient.invalidateQueries({ queryKey: ['applications', 'config'] });
    queryClient.invalidateQueries({ queryKey: ['applications', 'mine'] });
    queryClient.invalidateQueries({ queryKey: ['passes'] });
  };

  const saveSettings = useMutation({
    mutationFn: () => adminApi.applications.updateSettings(settingsDraft),
    onSuccess: (nextSettings) => {
      toast.success('Настройки заявок сохранены');
      queryClient.setQueryData(['admin', 'applications', 'settings'], nextSettings);
      setSettingsDraft({
        isEnabled: nextSettings.isEnabled,
        totalSlots: nextSettings.totalSlots,
        maxVideoSizeMb: nextSettings.maxVideoSizeMb,
      });
      setSettingsDirty(false);
      invalidate();
    },
    onError: (error: AxiosError<{ error?: string }>) => {
      if (error.response?.data?.error === 'slot_count_below_accepted') {
        toast.error('Количество слотов не может быть меньше уже принятых заявок');
      } else {
        toast.error('Не удалось сохранить настройки');
      }
    },
  });

  const resetSlots = useMutation({
    mutationFn: adminApi.applications.resetSlots,
    onMutate: () => {
      toast.loading('Сбрасываем занятые слоты…', { id: 'reset-application-slots' });
    },
    onSuccess: (nextSettings) => {
      toast.success('Занятые слоты сброшены. Новый цикл приёма начат.', {
        id: 'reset-application-slots',
      });
      queryClient.setQueryData(['admin', 'applications', 'settings'], nextSettings);
      setResetSlotsOpen(false);
      invalidate();
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      toast.error(error.response?.data?.message ?? 'Не удалось сбросить занятые слоты', {
        id: 'reset-application-slots',
      });
    },
  });

  const changeStatus = useMutation({
    mutationFn: ({ id, nextStatus }: { id: number; nextStatus: Exclude<ApplicationStatus, 'accepted'> }) =>
      adminApi.applications.updateStatus(id, { status: nextStatus }),
    onSuccess: (result, variables) => {
      toast.success(`Статус изменён: ${statusLabel[result.status]}`);
      setSelected((current) => (current ? { ...current, status: result.status } : current));
      if (variables.nextStatus === 'rejected') setRejecting(null);
      invalidate();
    },
    onError: (error: AxiosError<{ error?: string }>) => {
      const code = error.response?.data?.error;
      if (code === 'no_available_slots') toast.error('Свободных слотов больше нет');
      else if (code === 'application_not_found') toast.error('Заявка не найдена');
      else toast.error('Не удалось изменить статус заявки');
    },
  });

  const acceptApplication = useMutation({
    mutationFn: ({
      id,
      serverId,
      expiry,
    }: {
      id: number;
      serverId: number | null;
      expiry: PassExpiryDraft;
    }) =>
      adminApi.applications.updateStatus(id, {
        status: 'accepted',
        serverId,
        ...toPassExpiryPayload(expiry),
      }),
    onSuccess: () => {
      toast.success('Заявка принята, проходка выдана');
      setAccepting(null);
      setAcceptServerId('');
      setAcceptExpiry(createPassExpiryDraft());
      invalidate();
    },
    onError: (error: AxiosError<{ error?: string; message?: string }>) => {
      const code = error.response?.data?.error;
      const messages: Record<string, string> = {
        no_available_slots: 'Свободных слотов больше нет',
        application_not_found: 'Заявка не найдена',
        invalid_application_nickname: 'В заявке указан некорректный Minecraft-ник',
        nickname_taken: 'Этот Minecraft-ник уже привязан к другому пользователю',
        application_nickname_mismatch: 'Ник в заявке не совпадает с уже привязанным Minecraft-ником пользователя',
        server_not_found: 'Выбранный сервер не найден',
        invalid_pass_expiry: 'Укажите корректный срок проходки',
        ambiguous_pass_expiry: 'Выберите только один способ задания срока проходки',
        pass_scope_ambiguous: 'У пользователя несколько проходок. Выберите конкретный сервер',
        pass_is_infinite: 'У пользователя уже есть бессрочная проходка',
      };
      toast.error(messages[code ?? ''] ?? error.response?.data?.message ?? 'Не удалось принять заявку и выдать проходку');
      invalidate();
    },
  });

  const deleteApplication = useMutation({
    mutationFn: (application: AdminApplication) => adminApi.applications.remove(application.id),
    onSuccess: (_, deletedApplication) => {
      toast.success(`Заявка #${deletedApplication.id} удалена`);
      setSelected((current) => (current?.id === deletedApplication.id ? null : current));
      setAccepting((current) => (current?.id === deletedApplication.id ? null : current));
      setDeleting(null);
      invalidate();
    },
    onError: (error: AxiosError<{ error?: string; message?: string }>) => {
      const code = error.response?.data?.error;
      if (code === 'application_not_found') {
        toast.error('Заявка уже удалена или не найдена');
        setDeleting(null);
        invalidate();
      } else {
        toast.error(error.response?.data?.message ?? 'Не удалось удалить заявку');
      }
    },
  });

  function openAcceptDialog(application: AdminApplication) {
    setAccepting(application);
    setAcceptServerId('');
    setAcceptExpiry(createPassExpiryDraft());
    setSelected(null);
  }

  function closeDeleteDialog() {
    if (deleteApplication.isPending) return;
    setDeleting(null);
  }

  const settings = settingsQuery.data;
  const rows = listQuery.data?.rows ?? [];
  const totalPages = Math.max(1, Math.ceil((listQuery.data?.total ?? 0) / PER_PAGE));

  useEffect(() => {
    if (!listQuery.isFetching && page > totalPages) setPage(totalPages);
  }, [listQuery.isFetching, page, totalPages]);

  const settingsUnavailable = settingsQuery.isError || !settings;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-extrabold text-eol-white">Заявки</h1>
        <p className="mt-1 text-sm text-eol-desc">Настройка формы и рассмотрение заявок пользователей.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Настройки приёма</CardTitle>
          <CardDescription>
            Принятая заявка занимает один слот. Выключение формы не влияет на уже поданные заявки.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {settingsQuery.isError && (
            <p className="rounded-lg border border-eol-red/20 bg-eol-red/5 p-3 text-sm text-eol-red">
              Не удалось загрузить настройки заявок. Обновите страницу и попробуйте снова.
            </p>
          )}
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-white/5 bg-eol-black/30 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-eol-desc">Всего слотов</p>
              <p className="mt-2 text-2xl font-extrabold text-eol-white">{settings?.totalSlots ?? '—'}</p>
            </div>
            <div className="rounded-lg border border-white/5 bg-eol-black/30 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-eol-desc">Занято</p>
              <p className="mt-2 text-2xl font-extrabold text-eol-pink">{settings?.acceptedCount ?? '—'}</p>
            </div>
            <div className="rounded-lg border border-white/5 bg-eol-black/30 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-eol-desc">Свободно</p>
              <p className="mt-2 text-2xl font-extrabold text-eol-green">{settings?.availableSlots ?? '—'}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-4">
            <label className="flex min-h-10 items-center gap-3 rounded-md border border-white/10 bg-eol-black/30 px-3">
              <input
                type="checkbox"
                checked={settingsDraft.isEnabled}
                disabled={settingsUnavailable || saveSettings.isPending || resetSlots.isPending}
                onChange={(event) => updateSettingsDraft({ isEnabled: event.target.checked })}
              />
              <span className="text-sm font-semibold text-eol-white">Приём заявок открыт</span>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase tracking-wider text-eol-desc">Количество слотов</span>
              <NumberInput
                value={settingsDraft.totalSlots}
                onChange={(value) => updateSettingsDraft({ totalSlots: value })}
                min={0}
                max={10_000}
                disabled={settingsUnavailable || saveSettings.isPending || resetSlots.isPending}
                className="w-40"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase tracking-wider text-eol-desc">Макс. видео, МБ</span>
              <NumberInput
                value={settingsDraft.maxVideoSizeMb}
                onChange={(value) => updateSettingsDraft({ maxVideoSizeMb: value })}
                min={1}
                max={500}
                disabled={settingsUnavailable || saveSettings.isPending || resetSlots.isPending}
                className="w-40"
              />
            </label>
            <Button
              onClick={() => saveSettings.mutate()}
              disabled={settingsUnavailable || saveSettings.isPending || resetSlots.isPending || !settingsDirty}
              className="gap-2"
            >
              <Save className="h-4 w-4" /> Сохранить
            </Button>
            <Button
              variant="outline"
              onClick={() => setResetSlotsOpen(true)}
              disabled={settingsUnavailable || saveSettings.isPending || resetSlots.isPending}
              className="gap-2 border-eol-pink/30 text-eol-pink hover:bg-eol-pink/10"
            >
              <RotateCcw className="h-4 w-4" /> Сбросить занятые слоты
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="mb-4 mt-8 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Inbox className="h-5 w-5 text-eol-pink" />
          <h2 className="text-xl font-bold text-eol-white">Полученные заявки</h2>
        </div>
        <Select
          className="w-48"
          aria-label="Фильтр заявок по статусу"
          value={status}
          onChange={(event) => {
            setStatus(event.target.value as typeof status);
            setPage(1);
          }}
        >
          <option value="all">Все статусы</option>
          <option value="pending">На рассмотрении</option>
          <option value="accepted">Принятые</option>
          <option value="rejected">Отклонённые</option>
        </Select>
      </div>

      {listQuery.isLoading ? (
        <p className="text-sm text-eol-desc">Загрузка…</p>
      ) : listQuery.isError ? (
        <Card>
          <CardContent className="py-10 text-center text-eol-red">
            Не удалось загрузить список заявок. Обновите страницу и попробуйте снова.
          </CardContent>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-eol-desc">Заявок с таким статусом пока нет.</CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {rows.map((application) => (
            <Card key={application.id}>
              <CardContent className="flex flex-wrap items-start gap-4 py-4">
                {application.avatar ? (
                  <img src={application.avatar} alt="" className="h-12 w-12 rounded-full" />
                ) : (
                  <div className="grid h-12 w-12 place-items-center rounded-full bg-eol-pink/10 font-bold text-eol-pink">
                    {(application.globalName ?? application.username ?? '?').slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-bold text-eol-white">{application.nickname}</h3>
                    <Badge variant={statusVariant[application.status]}>{statusLabel[application.status]}</Badge>
                    <Badge variant="outline">{application.age} лет</Badge>
                  </div>
                  <p className="mt-1 truncate text-sm text-eol-desc">
                    Discord: {application.globalName ?? application.username ?? 'Неизвестный пользователь'}
                    {application.name ? ` · Имя: ${application.name}` : ''}
                  </p>
                  <p className="mt-2 line-clamp-2 whitespace-pre-wrap [overflow-wrap:anywhere] text-sm text-eol-white/80">{application.about}</p>
                  <p className="mt-2 text-xs text-eol-desc">
                    Отправлена {formatDateTime(application.submittedAt)} · Видео {formatBytes(application.videoSizeBytes)}
                  </p>
                </div>
                <div className="flex w-full items-center gap-2 sm:w-auto sm:flex-col sm:items-stretch">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setSelected(application)}
                    disabled={deleteApplication.isPending && deleting?.id === application.id}
                    className="flex-1 gap-2 sm:flex-none"
                  >
                    <Eye className="h-4 w-4" /> Рассмотреть
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setDeleting(application)}
                    disabled={deleteApplication.isPending}
                    className="flex-1 gap-2 sm:flex-none"
                    aria-label={`Удалить заявку #${application.id}`}
                  >
                    <Trash2 className="h-4 w-4" /> Удалить
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {(listQuery.data?.total ?? 0) > PER_PAGE && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            Назад
          </Button>
          <span className="text-sm text-eol-desc">Страница {page} из {totalPages}</span>
          <Button
            variant="secondary"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            Вперёд
          </Button>
        </div>
      )}

      <Dialog open={Boolean(selected)} onOpenChange={(open) => { if (!open) setSelected(null); }}>
        <DialogContent className="max-h-[92vh] w-[calc(100%-2rem)] max-w-3xl overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex flex-wrap items-center gap-2">
                  Заявка #{selected.id}
                  <Badge variant={statusVariant[selected.status]}>{statusLabel[selected.status]}</Badge>
                </DialogTitle>
                <DialogDescription>
                  {selected.globalName ?? selected.username ?? 'Discord-пользователь'} · {formatDateTime(selected.submittedAt)}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-5">
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded-lg bg-white/5 p-3">
                    <p className="text-xs uppercase tracking-wider text-eol-desc">Имя</p>
                    <p className="mt-1 font-semibold text-eol-white">{selected.name || 'Не указано'}</p>
                  </div>
                  <div className="rounded-lg bg-white/5 p-3">
                    <p className="text-xs uppercase tracking-wider text-eol-desc">Никнейм</p>
                    <p className="mt-1 font-semibold text-eol-white">{selected.nickname}</p>
                  </div>
                  <div className="rounded-lg bg-white/5 p-3">
                    <p className="text-xs uppercase tracking-wider text-eol-desc">Возраст</p>
                    <p className="mt-1 font-semibold text-eol-white">{selected.age}</p>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-eol-desc">О себе</p>
                  <p className="mt-2 whitespace-pre-wrap [overflow-wrap:anywhere] rounded-lg border border-white/5 bg-eol-black/30 p-4 leading-7 text-eol-white/85">
                    {selected.about}
                  </p>
                </div>

                <div>
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <FileVideo2 className="h-4 w-4 text-eol-pink" />
                    <p className="min-w-0 truncate text-sm font-semibold text-eol-white">{selected.videoOriginalName}</p>
                    <span className="text-xs text-eol-desc">{formatBytes(selected.videoSizeBytes)}</span>
                  </div>
                  <ApplicationVideo application={selected} />
                </div>
              </div>

              <DialogFooter className="flex-wrap">
                {selected.status !== 'pending' && (
                  <Button
                    variant="secondary"
                    onClick={() => changeStatus.mutate({ id: selected.id, nextStatus: 'pending' })}
                    disabled={changeStatus.isPending}
                    className="gap-2"
                  >
                    <RotateCcw className="h-4 w-4" /> Вернуть на рассмотрение
                  </Button>
                )}
                {selected.status !== 'rejected' && (
                  <Button
                    variant="outline"
                    onClick={() => setRejecting(selected)}
                    disabled={changeStatus.isPending || acceptApplication.isPending}
                    className="gap-2"
                  >
                    <X className="h-4 w-4" /> Отклонить
                  </Button>
                )}
                {selected.status === 'pending' && (
                  <Button
                    onClick={() => openAcceptDialog(selected)}
                    disabled={changeStatus.isPending || acceptApplication.isPending || (settings?.availableSlots ?? 0) <= 0}
                    className="gap-2"
                  >
                    <Check className="h-4 w-4" /> Принять
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(rejecting)}
        onOpenChange={(open) => {
          if (!open && !changeStatus.isPending) setRejecting(null);
        }}
      >
        <DialogContent className="w-[calc(100%-2rem)] max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 shrink-0 text-eol-red" /> Отклонить заявку?
            </DialogTitle>
            <DialogDescription>
              Заявка #{rejecting?.id} от{' '}
              <strong className="text-eol-white">
                {rejecting?.globalName ?? rejecting?.username ?? rejecting?.nickname}
              </strong>{' '}
              получит статус «Отклонена».
            </DialogDescription>
          </DialogHeader>

          {rejecting && (
            <div className="space-y-3">
              <div className="rounded-md border border-eol-red/20 bg-eol-red/5 p-3 text-sm text-eol-desc">
                <p>
                  Minecraft-ник: <strong className="break-all text-eol-white">{rejecting.nickname}</strong>
                </p>
                <p className="mt-1">
                  Текущий статус: <span className="text-eol-white">{statusLabel[rejecting.status]}</span>
                </p>
              </div>
              <p className="rounded-md border border-eol-red/30 bg-eol-red/10 p-3 text-sm font-semibold text-eol-red">
                {rejecting.status === 'accepted'
                  ? 'Связанная проходка будет отозвана, а занятый заявкой слот освободится.'
                  : 'Заявка будет отклонена без выдачи проходки.'}
              </p>
            </div>
          )}

          <DialogFooter className="flex-col-reverse sm:flex-row">
            <Button
              variant="secondary"
              onClick={() => setRejecting(null)}
              disabled={changeStatus.isPending}
              className="w-full sm:w-auto"
            >
              Отмена
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                rejecting && changeStatus.mutate({ id: rejecting.id, nextStatus: 'rejected' })
              }
              disabled={!rejecting || changeStatus.isPending}
              className="w-full gap-2 sm:w-auto"
            >
              <X className="h-4 w-4" />
              {changeStatus.isPending ? 'Отклоняем…' : 'Отклонить заявку'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(accepting)}
        onOpenChange={(open) => {
          if (!open && !acceptApplication.isPending) setAccepting(null);
        }}
      >
        <DialogContent className="max-h-[92vh] w-[calc(100%-2rem)] max-w-xl overflow-y-auto">
          {accepting && (
            <>
              <DialogHeader>
                <DialogTitle>Принять заявку и выдать проходку</DialogTitle>
                <DialogDescription>
                  {accepting.nickname} · заявка #{accepting.id}. Выберите сервер и срок действия проходки.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-5">
                <label className="block">
                  <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-eol-desc">
                    Сервер
                  </span>
                  <Select
                    value={acceptServerId}
                    disabled={acceptApplication.isPending}
                    onChange={(event) => setAcceptServerId(event.target.value)}
                  >
                    <option value="">Все серверы (глобальная проходка)</option>
                    {serversQuery.data?.map((server) => (
                      <option key={server.id} value={String(server.id)} disabled={!server.isEnabled}>
                        {server.name}{server.isEnabled ? '' : ' (выключен)'}
                      </option>
                    ))}
                  </Select>
                  {serversQuery.isLoading && <p className="mt-1.5 text-xs text-eol-desc">Загружаем серверы…</p>}
                  {serversQuery.isError && (
                    <p className="mt-1.5 text-xs text-eol-red">
                      Не удалось загрузить список серверов. Можно выдать глобальную проходку или повторить позже.
                    </p>
                  )}
                </label>

                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-eol-desc">
                    Срок действия
                  </p>
                  <PassExpiryPicker
                    value={acceptExpiry}
                    onChange={setAcceptExpiry}
                    disabled={acceptApplication.isPending}
                    durationMode="from-now"
                  />
                </div>
              </div>

              <DialogFooter className="flex-wrap">
                <Button
                  variant="secondary"
                  onClick={() => setAccepting(null)}
                  disabled={acceptApplication.isPending}
                >
                  Отмена
                </Button>
                <Button
                  onClick={() =>
                    acceptApplication.mutate({
                      id: accepting.id,
                      serverId: acceptServerId ? Number(acceptServerId) : null,
                      expiry: acceptExpiry,
                    })
                  }
                  disabled={acceptApplication.isPending || !isPassExpiryValid(acceptExpiry)}
                  className="gap-2"
                >
                  <Check className="h-4 w-4" />
                  {acceptApplication.isPending ? 'Принимаем…' : 'Принять и выдать'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(deleting)} onOpenChange={(open) => { if (!open) closeDeleteDialog(); }}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 shrink-0 text-eol-red" /> Удалить заявку?
            </DialogTitle>
            <DialogDescription>
              Заявка #{deleting?.id} от{' '}
              <strong className="text-eol-white">
                {deleting?.globalName ?? deleting?.username ?? deleting?.nickname}
              </strong>{' '}
              и загруженное видео будут удалены. Это действие нельзя отменить.
            </DialogDescription>
          </DialogHeader>

          {deleting && (
            <div className="space-y-3">
              <div className="rounded-md border border-eol-red/20 bg-eol-red/5 p-3 text-sm text-eol-desc">
                <p>
                  Minecraft-ник: <strong className="break-all text-eol-white">{deleting.nickname}</strong>
                </p>
                <p className="mt-1">
                  Статус: <span className="text-eol-white">{statusLabel[deleting.status]}</span>
                </p>
              </div>
              <p className="rounded-md border border-eol-red/30 bg-eol-red/10 p-3 text-sm font-semibold text-eol-red">
                {deleting.status === 'accepted'
                  ? 'Связанная проходка будет отозвана, слот освободится.'
                  : 'Если заявку уже успели принять, связанная проходка также будет отозвана, а слот освободится.'}
              </p>
            </div>
          )}

          <DialogFooter className="flex-col-reverse sm:flex-row">
            <Button
              variant="secondary"
              onClick={closeDeleteDialog}
              disabled={deleteApplication.isPending}
              className="w-full sm:w-auto"
            >
              Отмена
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleting && deleteApplication.mutate(deleting)}
              disabled={!deleting || deleteApplication.isPending}
              className="w-full gap-2 sm:w-auto"
            >
              <Trash2 className="h-4 w-4" />
              {deleteApplication.isPending ? 'Удаление…' : 'Удалить заявку'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={resetSlotsOpen}
        onOpenChange={(open) => {
          if (!resetSlots.isPending) setResetSlotsOpen(open);
        }}
      >
        <DialogContent className="w-[calc(100%-2rem)] max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 shrink-0 text-eol-pink" /> Сбросить занятые слоты?
            </DialogTitle>
            <DialogDescription>
              Счётчик занятых слотов станет равен 0, и начнётся новый цикл приёма заявок.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <p className="rounded-md border border-eol-pink/20 bg-eol-pink/5 p-3 text-sm text-eol-desc">
              Все заявки, выданные проходки и роли Discord сохранятся без изменений.
            </p>
            <p className="text-sm text-eol-desc">
              Текущее значение «Занято»: <strong className="text-eol-white">{settings?.acceptedCount ?? '—'}</strong>.
              После сброса оно станет равно 0.
            </p>
          </div>

          <DialogFooter className="flex-col-reverse sm:flex-row">
            <Button
              variant="secondary"
              onClick={() => setResetSlotsOpen(false)}
              disabled={resetSlots.isPending}
              className="w-full sm:w-auto"
            >
              Отмена
            </Button>
            <Button
              onClick={() => resetSlots.mutate()}
              disabled={resetSlots.isPending}
              className="w-full gap-2 sm:w-auto"
            >
              <RotateCcw className="h-4 w-4" />
              {resetSlots.isPending ? 'Сбрасываем…' : 'Начать новый цикл'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
